import React, { ReactNode, useState, ChangeEvent, } from 'react';
import classNames from 'classnames';
import ClickAwayListener from 'react-click-away-listener';
import { PartialsBackgroundVariants } from './PartialsBackgroundVariants';

export interface FileInputProps {
  label: string;
  name: string;
  onValueChange: (inputValue: File | File[]) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  prefixIcon?: ReactNode;
  postfixIcon?: ReactNode;
  required?: boolean;
  requiredText?: string | ReactNode | Element;
  autoFocus?: boolean;
  backgroundVariant?: PartialsBackgroundVariants;
  multiple?: boolean;
  disabled?: boolean;
};

export const FileInput = (props: FileInputProps) => {
  const {
    label,
    name,
    error,
    onValueChange,
    className,
    prefixIcon,
    postfixIcon,
    requiredText,
    multiple,
    autoFocus,
    placeholder,
    required = false,
    backgroundVariant = 'light',
    disabled = false
  } = props;

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState<any>();
  const [fileNames, setFileNames] = useState('');

  const id = `insuranceFileInput-${name}`;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.files) {
      const files = Array.from(e.currentTarget.files);
      const fileNames = files.map(file => file.name);

      setFileNames(fileNames.join(', '));
      setInputValue(Array.from(e.currentTarget.files))
      onValueChange(files)
    }
  }

  const inputFiledModifiers = {
    'insuranceInput__field--prefixIcon': prefixIcon,
    'insuranceInput__field--postfixIcon': postfixIcon,
    'insuranceInput__field--light': backgroundVariant === 'light',
    'insuranceInput__field--blueLight': backgroundVariant === 'blue-light',
  }

  const renderPrefixIcon = () => (
    prefixIcon && (
      <span className="insuranceInput__icon">
        {prefixIcon}
      </span>
    )
  )

  const renderFileInputMimic = () => (
    <div
      className={classNames('insuranceInput__field', 'insuranceFileInput__fieldMimic', {
        ...inputFiledModifiers,
        "insuranceFileInput__fieldMimic--focus": isInputFocused
      })}
    >
      {fileNames
        ? fileNames
        : placeholder && (
          <span className='insuranceFileInput__fieldMimicPlaceholder'>
            {placeholder}
          </span>
        )
      }
    </div>
  )

  const renderLabel = () => (
    <label
      className={classNames('insuranceInput__label', {
        "insuranceInput__label--focus": !!inputValue || isInputFocused || placeholder,
        'insuranceInput__label--prefixIcon': prefixIcon,
      })}
      htmlFor={id}
    >
      {label} {required && requiredText}
    </label>
  )

  const renderPostfixIcon = () => (
    postfixIcon && (
      <span className="insuranceInput__icon">
        {postfixIcon}
      </span>
    )
  )

  const renderError = () => (
    error && (
      <div className='insuranceInput__error'>
        <p id={"errorId-" + id} className="fs-6 insuranceInput__errorText">
          {error}
        </p>
      </div>
    )
  )

  return (
    <ClickAwayListener onClickAway={() => setIsInputFocused(false)}>
      <div className={classNames("insuranceInput insuranceFileInput", className, {
        "insuranceInput--error": error,
        "insuranceInput--disabled": disabled,
      })}>
        {renderLabel()}
        <div className="insuranceInput__inner">
          {renderPrefixIcon()}
          {renderFileInputMimic()}
          <input
            className={classNames('insuranceInput__field insuranceFileInput__field', {
              ...inputFiledModifiers,
            })}
            onFocus={() => setIsInputFocused(true)}
            onChange={handleChange}
            id={id}
            name={name}
            type="file"
            required={required}
            aria-describedby={"errorId-" + id}
            autoFocus={autoFocus}
            placeholder={placeholder}
            multiple={multiple}
            disabled={disabled}
          />
          {renderPostfixIcon()}
        </div>
        {renderError()}
      </div>
    </ClickAwayListener>
  )
}
